import React, { useState, useEffect, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../../commonButton";
import { formatMomentDate } from "../../../utils/dateHelper";
import withModal from "../../withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../form/InputField";

const CompanyCertificateModalInner = ({ onClose, onSubmit, certificate, isLoading }) => {
  const [formData, setFormData] = useState({
    title: "",
    provider: "",
    issued_date: "",
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);
  const fileInputRef = useRef(null);
  const toast=useToast();
  useEffect(() => {
    if (certificate) {
      setFormData({
        title: certificate.title || "",
        provider: certificate.provider || "",
        issued_date: certificate.issued_date ? formatMomentDate(certificate.issued_date, "YYYY-MM-DD") : "",
      });
      setFile(null);
      setFilePreview(certificate.file?.file_url || null);
      setRemoveFile(false);
    } else {
      setFormData({
        title: "",
        provider: "",
        issued_date: "",
      });
      setFile(null);
      setFilePreview(null);
      setRemoveFile(false);
    }
  }, [certificate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }
      setFile(selectedFile);
      setFilePreview(URL.createObjectURL(selectedFile));
      setRemoveFile(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFilePreview(null);
    setRemoveFile(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!formData.title) {
      toast.error("Please enter a title");
      return;
    }

    if (!formData.provider) {
      toast.error("Please enter who issued this certificate");
      return;
    }

    if (!formData.issued_date) {
      toast.error("Please select an issue date");
      return;
    }

    const submitData = new FormData();
    submitData.append("title", formData.title);
    submitData.append("provider", formData.provider);
    if (formData.issued_date) {
      submitData.append("issued_date", formData.issued_date);
    }

    if (file) {
      submitData.append("file", file);
    } else if (removeFile) {
      submitData.append("remove_file", "true");
    }

    onSubmit(submitData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Modal.Body className="grid row-gap-3">
        <InputField
          className="g-col-12"
          label={{ name: "Name" }}
          required
          input={{
            type: "text",
            name: "title",
            value: formData.title,
            onChange: handleChange,
            placeholder: "Ex: ISO 9001:2015",
          }}
        />
        <InputField
          className="g-col-6"
          label={{ name: "Issuing Company" }}
          required
          input={{
            type: "text",
            name: "provider",
            value: formData.provider,
            onChange: handleChange,
            placeholder: "Ex: Certification Body",
          }}
        />
        <InputField
          className="g-col-6"
          label={{ name: "Issue Date" }}
          required
          input={{
            type: "date",
            name: "issued_date",
            value: formData.issued_date,
            onChange: handleChange,
            max: new Date().toLocaleDateString('en-CA'),
          }}
        />
        <Form.Group className="g-col-12">
          <Form.Label>Certificate Image</Form.Label>
          <div className="d-flex align-items-center gap-3">
            <div 
               className="certificate-file-upload border rounded-2 d-flex align-items-center justify-content-center cursor-pointer overflow-hidden"
               style={{ width: "120px", height: "120px", backgroundColor: "#f8f9fa" }}
               onClick={() => fileInputRef.current?.click()}
            >
              {filePreview ? (
                <img src={filePreview} alt="Certificate Preview" className="w-100 h-100 object-fit-cover" />
              ) : (
                <span className="text-muted small text-center px-2">Click to Upload Image</span>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="d-none"
            />
            {filePreview && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleRemoveFile}
              >
                Remove
              </button>
            )}
          </div>
          <Form.Text className="text-muted d-block mt-2">Max size: 5MB. Supported: Images (JPG, PNG, etc.)</Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <CommonButton
          type="button"
          variant="outline"
          title="Cancel"
          onClick={onClose}
          disabled={isLoading}
        />
        <CommonButton
          type="submit"
          variant="primary"
          title={certificate ? "Update" : "Save"}
          isLoading={isLoading}
        />
      </Modal.Footer>
    </Form>
  );
};

export default withModal(CompanyCertificateModalInner);

